import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  DateTime,
} from "luxon";

import {
  useAuth,
} from "../context/AuthContext";

import {
  getUpcomingBookings,
  getUserOrders,
} from "../services/bookingService";

import DashboardHeader
  from "../components/dashboard/DashboardHeader";

import DashboardStats
  from "../components/dashboard/DashboardStats";

import QuickActions
  from "../components/dashboard/QuickActions";

import NextBookingCard
  from "../components/dashboard/NextBookingCard";

import {
  mergeContinuousSlots,
} from "../utils/bookingUtils";

import {
  BUSINESS_TZ,
  getCurrentHourET,
  getTodayET,
} from "../utils/timeUtils";

function DashboardPage() {

  const navigate =
    useNavigate();

  const {
    currentUser,
    isAuthenticated,
  } = useAuth();

  const [
    upcomingBookings,
    setUpcomingBookings,
  ] = useState([]);

  const [
    recentActivity,
    setRecentActivity,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  /* ---------------------------------- */
  /* SLOT DATETIME */
  /* ---------------------------------- */

  function getSlotDateTime(
    slot
  ) {

    return DateTime.fromISO(

      `${slot.date}T${String(slot.startHour || 0).padStart(2, "0")}:00:00`,

      {
        zone:
          BUSINESS_TZ,
      }
    );
  }

  /* ---------------------------------- */
  /* CHECK IF SLOT IS PAST */
  /* ---------------------------------- */

  function isPastSlot(
    slot
  ) {

    const todayET =
      getTodayET();

    const currentHourET =
      getCurrentHourET();

    /* PAST DATE */

    if (
      slot.date < todayET
    ) {

      return true;
    }

    /* FUTURE DATE */

    if (
      slot.date > todayET
    ) {

      return false;
    }

    /* TODAY */

    return (
      (slot.endHour || 0) <=
      currentHourET
    );
  }

  useEffect(() => {

    async function loadDashboardData() {

      if (!currentUser) {

        setLoading(false);

        return;
      }

      try {

        const upcomingResponse =
          await getUpcomingBookings({

            userId:
              currentUser.uid,
          });

        const historyResponse =
          await getUserOrders({

            userId:
              currentUser.uid,
          });

        setUpcomingBookings(

          upcomingResponse.orders || []
        );

        setRecentActivity(

          historyResponse.orders || []
        );

      } catch (error) {

        console.error(error);

      } finally {

        setLoading(false);
      }
    }

    loadDashboardData();

  }, [currentUser]);

  /* ---------------------------------- */
  /* TOTAL HOURS PLAYED */
  /* ONLY PAST BOOKINGS */
  /* ---------------------------------- */

  const totalHours =
    useMemo(() => {

      return recentActivity.reduce(

        (
          total,
          booking
        ) => {

          const completedHours =

            (booking.slots || [])

              .filter((slot) =>
                isPastSlot(slot)
              )

              .reduce(

                (
                  sum,
                  slot
                ) =>

                  sum +
                  (
                    slot.duration ||
                    1
                  ),

                0
              );

          return (
            total +
            completedHours
          );

        },

        0
      );

    }, [recentActivity]);

  /* ---------------------------------- */
  /* FIND NEXT BOOKING */
  /* ---------------------------------- */

  const nextBooking =
    useMemo(() => {

      const allSlots =
        upcomingBookings.flatMap(
          (booking) =>
            booking.slots || []
        );

      const futureSlots =
        allSlots.filter(
          (slot) =>
            !isPastSlot(slot)
        );

      if (
        futureSlots.length === 0
      ) {

        return null;
      }

      const sortedSlots =
        futureSlots.sort(

          (a, b) => {

            const aDate =
              getSlotDateTime(a);

            const bDate =
              getSlotDateTime(b);

            return (
              aDate.toMillis() -
              bDate.toMillis()
            );
          }
        );

      const earliestSlot =
        sortedSlots[0];

      const sameCourtSlots =
        sortedSlots.filter(
          (slot) =>

            slot.date ===
            earliestSlot.date &&

            slot.courtId ===
            earliestSlot.courtId
        );

      const mergedSlots =
        mergeContinuousSlots(
          sameCourtSlots
        );

      return (
        mergedSlots[0] ||
        null
      );

    }, [upcomingBookings]);

  /* ---------------------------------- */
  /* AUTH */
  /* ---------------------------------- */

  if (!isAuthenticated) {

    return (

      <div style={styles.authPage}>

        <div style={styles.authCard}>

          <h2 style={styles.authTitle}>
            Sign in to view dashboard
          </h2>

          <button

            onClick={() =>
              navigate("/signin")
            }

            style={styles.authButton}
          >
            Sign In
          </button>

        </div>

      </div>
    );
  }

  /* ---------------------------------- */
  /* LOADING */
  /* ---------------------------------- */

  if (loading) {

    return (

      <div style={styles.loading}>
        Loading dashboard...
      </div>
    );
  }

  return (

    <div style={styles.page}>

      <div style={styles.container}>

        <DashboardHeader />

        <DashboardStats

          upcomingCount={
            upcomingBookings.length
          }

          totalHours={
            totalHours
          }
        />

        <div style={styles.topGrid}>

          <QuickActions />

          <NextBookingCard

            booking={
              nextBooking
            }
          />

        </div>

      </div>

    </div>
  );
}

const styles = {

  page: {

    minHeight: "100vh",

    padding: "40px 20px",

    background:
      "linear-gradient(111.1deg, rgb(0,40,70) -4.8%, rgb(255,229,115) 82.7%, rgb(255,203,15) 97.2%)",
  },

  container: {

    maxWidth: "1200px",

    margin: "0 auto",
  },

  topGrid: {

    display: "grid",

    gridTemplateColumns:
      "1fr 1fr",

    gap: "24px",

    marginBottom: "24px",
  },

  loading: {

    minHeight: "100vh",

    display: "flex",

    justifyContent: "center",

    alignItems: "center",

    color: "#fff",

    fontSize: "18px",
  },

  authPage: {

    minHeight: "100vh",

    display: "flex",

    justifyContent: "center",

    alignItems: "center",
  },

  authCard: {

    background:
      "rgba(0,40,70,0.82)",

    padding: "40px",

    borderRadius: "24px",

    textAlign: "center",
  },

  authTitle: {

    color: "#fff",

    marginBottom: "24px",
  },

  authButton: {

    padding: "14px 24px",

    borderRadius: "999px",

    border: "none",

    cursor: "pointer",

    fontWeight: "700",

    background:
      "linear-gradient(90deg, #00ff88, #00d4ff)",
  },
};

export default DashboardPage;